/**
 * WinnerPodium3D — the lazy-loaded, failure-guarded entry point for the
 * celebratory 3D winner podium shown on the post-round + final-standings
 * screens.
 *
 * Like {@link RoomBackdrop3D} this wrapper is Three.js-FREE and `React.lazy`s
 * the heavy {@link WinnerPodium3DScene} so the WebGL chunk only loads when a
 * ceremony actually renders. On chunk/WebGL failure it renders the optional
 * `fallback` (the callers keep their existing 2D standings UI, so the moment
 * still works without the 3D).
 *
 * The 3D canvas + name/score captions fill the host element you size via
 * `className` / `style`; pass the winners in RANK ORDER (1st, 2nd, 3rd).
 */
import { Suspense, lazy } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { CharacterIcon } from '@tt/types';
import { Lobby3DBoundary } from './Lobby3DBoundary';

const WinnerPodium3DScene = lazy(() => import('./WinnerPodium3DScene'));

export interface PodiumEntry {
  charId: string;
  name: string;
  icon: CharacterIcon;
  /** Score shown under the pedestal (table points). */
  points: number;
}

export interface WinnerPodium3DProps {
  /** Up to 3 winners in rank order: [1st, 2nd, 3rd]. */
  entries: PodiumEntry[];
  className?: string;
  style?: CSSProperties;
  /** Optional fallback shown if the 3D fails to load. Default: nothing. */
  fallback?: ReactNode;
}

export function WinnerPodium3D({ entries, className, style, fallback }: WinnerPodium3DProps) {
  if (entries.length === 0) return null;
  return (
    <Lobby3DBoundary fallback={fallback}>
      <Suspense fallback={fallback ?? null}>
        <WinnerPodium3DScene entries={entries} className={className} style={style} />
      </Suspense>
    </Lobby3DBoundary>
  );
}
